import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { AuthShell, Field } from "./Login";
import { Mail, CheckCircle2 } from "lucide-react";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("trainer");
  const [vereniging, setVereniging] = useState("");
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const r = await api.post("/auth/register", { name, email, password, role, vereniging });
      setDevLink(r.data.verify_link_dev || null);
      setDone(true);
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  };

  const notice = (
    <div className="mb-5 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="leap-logic-notice">
      <strong>Let op:</strong> de bevestigingsmail komt aan van <strong>Sprongchecker</strong> via onze mail-provider. Open de mail om je account te activeren — check ook je spamfolder.
    </div>
  );

  if (done) {
    return (
      <AuthShell title="Bijna klaar!" subtitle="Bevestig je e-mail">
        <div className="text-center py-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
            <Mail className="text-emerald-600" size={28}/>
          </div>
          <p className="text-slate-700 mb-3">We hebben een bevestigingsmail gestuurd naar <strong>{email}</strong>.</p>
          <p className="text-sm text-slate-500">De mail komt aan van <strong>Sprongchecker</strong>. Check ook je spamfolder.</p>
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900 text-left" data-testid="no-mail-notice">
            <strong>Geen mail ontvangen binnen 5 minuten?</strong> Stuur me een berichtje via de <Link to="/contact" className="underline font-semibold">Contact pagina</Link> — ik activeer je account dan handmatig.
          </div>
          {devLink && (
            <div className="mt-4 p-3 bg-slate-50 border rounded text-xs text-left break-all" data-testid="dev-verify-link">
              <div className="font-semibold mb-1">Dev modus — verificatie-link:</div>
              <a href={devLink} className="text-orange-600 underline">{devLink}</a>
            </div>
          )}
          <Link to="/login" className="mt-6 inline-block text-orange-600 hover:underline" data-testid="back-to-login">Terug naar inloggen</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Account aanmaken" subtitle="Sla je sprongen op en check ze in 1 klik" notice={notice}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Naam" value={name} onChange={setName} testid="register-name"/>
        <Field label="E-mail" type="email" value={email} onChange={setEmail} testid="register-email"/>
        <Field label="Wachtwoord (min 6 tekens)" type="password" value={password} onChange={setPassword} testid="register-password"/>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Ik ben een...</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setRole("trainer")} className={`flex-1 py-2.5 rounded-lg border-2 font-semibold text-sm ${role === "trainer" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600"}`} data-testid="role-trainer">Trainer</button>
            <button type="button" onClick={() => setRole("turner")} className={`flex-1 py-2.5 rounded-lg border-2 font-semibold text-sm ${role === "turner" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600"}`} data-testid="role-turner">Turner</button>
          </div>
        </div>
        <Field label="Verenigingsnaam (optioneel)" value={vereniging} onChange={setVereniging} testid="register-vereniging" placeholder="bv. TV Voorwaarts" required={false}/>
        {role === "turner" && vereniging && (
          <div className="text-xs text-slate-500 -mt-2">Een trainer van <strong>{vereniging}</strong> moet je nog goedkeuren voordat je in het overzicht verschijnt.</div>
        )}
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} required className="mt-0.5" data-testid="register-agree"/>
          <span>Ik ga akkoord met de <Link to="/voorwaarden" className="text-orange-600 underline">voorwaarden</Link> en heb de <Link to="/privacy" className="text-orange-600 underline">privacyverklaring</Link> gelezen.</span>
        </label>
        {err && <div className="text-red-600 text-sm" data-testid="register-error">{err}</div>}
        <button type="submit" disabled={loading || !agree} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-lg disabled:opacity-50" data-testid="register-submit">
          {loading ? "Bezig..." : "Account aanmaken"}
        </button>
        <div className="text-sm text-center text-slate-600">
          Al een account? <Link to="/login" className="text-orange-600 hover:underline" data-testid="goto-login">Inloggen</Link>
        </div>
      </form>
    </AuthShell>
  );
}
